import { Injectable } from '@angular/core';
import { BrandingService } from '../core/services/branding.service';
import { LeadService } from '../core/services/lead.service';
import {skipWhile, tap, withLatestFrom} from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadTrackingUrl } from '../core/models/tracking-url.model';

@Injectable({
  providedIn: 'root'
})
export class ResidentialLeadService {
  private identifier;
  private leadEmail;
  private forbiddenFormFieldNames = ['address', 'street_name', 'city', 'state', 'postal_code', 'first_name', 'last_name', 'birth_date_main'];
  private basicSectionFieldNames = ['address', 'unit', 'init_first_name', 'init_last_name', 'init_email'];
  private insuranceInfoSectionFieldNames = ['effective_date', 'number_of_insured', 'billing_strategy_id', 'deductible', 'deductible_fl'];
  private coverageLimitsSectionFieldNames = ['possessions_coverage_limit', 'liability_payments_limit', 'new_carrier'];
  private insuredDetailsSectionFieldNames = ['first_name', 'last_name', 'birth_date_main', 'birth_date_sub', 'email', 'contact_phone', 'street_name', 'suite_unit',
  'city', 'state' , 'postal_code'];
  private PAYMENT_SECTION_NAME = 'Payment Section';
  trackingUrlData: LeadTrackingUrl;
  constructor(
    private brandingService: BrandingService,
    private leadService: LeadService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.route.queryParams.pipe(
      skipWhile(() => brandingService.brandingInfo$ === null),
      withLatestFrom(brandingService.brandingInfo$)
    ).subscribe(([params, brandingInfo]) => {
      this.trackingUrlData = {
        landing_page: 'residential',
        campaign_source: params.campaign_source,
        campaign_medium: params.campaign_medium,
        campaign_term: params.campaign_term,
        campaign_content: params.campaign_content,
        campaign_name: params.campaign_name,
        branding_profile_id: brandingInfo.id
      };
    });
  }

  createLead(data) {
    const agencyId = this.brandingService.getBrandingInfoSnapshot().agency_id;
    const accountId = this.brandingService.getBrandingInfoSnapshot().account_id;
    const brandingId = this.brandingService.getBrandingInfoSnapshot().id;

    data.lead_event_attributes.data.branding_profile_id = brandingId;
    data.branding_profile_id = brandingId;

    if (data.email && (this.leadEmail !== data.email)) {
      this.leadEmail = data.email;
    }

    const body = {
      ...data,
      policy_type_id: 1,
      agency_id: agencyId,
      account_id: accountId,
      branding_profile_id: brandingId,
      identifier: this.identifier,
      email: this.leadEmail,
    };
    this.leadService.createLead(body).pipe(
      tap((res) => {
        if (!this.identifier) {
          this.identifier = res.identifier;
        }
      })
    ).subscribe();
  }

  convertFormDataToLead(formData) {
    const leadEventData: any = this.createLeadDataFromFormData(formData);
    const addressAttributes: any = this.createAddressAttributesFromFormData(formData);
    const profileAttributes: any = this.createProfileAttributesFromFormData(formData);

    return {
      email: formData.email,
      lead_event_attributes: {
        tag: '',
        data: leadEventData
      },
      profile_attributes: profileAttributes,
      address_attributes: addressAttributes,
      tracking_url: this.trackingUrlData
    };
  }

  getLeadSectionByFieldName(fieldName) {
    if (!fieldName) { return; }
    if (this.basicSectionFieldNames.includes(fieldName)) {
      return 'Basic Info Section';
    } else if (this.insuranceInfoSectionFieldNames.includes(fieldName)) {
      return 'Insurance Info Section';
    } else if (this.coverageLimitsSectionFieldNames.includes(fieldName) ||
      (fieldName.indexOf('option_') > -1) ||
      (fieldName.indexOf('carrier_') > -1)) {
      return 'Coverage Limits Section';
    } else if (this.insuredDetailsSectionFieldNames.includes(fieldName)) {
      return 'Insured Details Section';
    }
  }

  createPolicySubmitEvent() {
    this.createLead({
      email: this.leadEmail,
      lead_event_attributes: {
        tag: 'policy_submit',
        data: {
          last_visited_page: this.PAYMENT_SECTION_NAME
        }
      },
    });
  }

  createPaymentSuccessEvent() {
    this.createLead({
      email: this.leadEmail,
      lead_event_attributes: {
        tag: 'payment_success',
        data: {
          last_visited_page: this.PAYMENT_SECTION_NAME
        }
      },
    });
  }

  createPaymentErrorEvent() {
    this.createLead({
      email: this.leadEmail,
      lead_event_attributes: {
        tag: 'payment_error',
        data: {
          last_visited_page: this.PAYMENT_SECTION_NAME
        }
      },
    });
  }

  createCompleteCard(value) {
    this.createLead({
      email: this.leadEmail,
      lead_event_attributes: {
        tag: 'card_filled',
        data: {
          card_brand: value.brand,
          last_visited_page: this.PAYMENT_SECTION_NAME
        }
      },
    });
  }

  createBackEvent() {
    this.createLead({
      email: this.leadEmail,
      lead_event_attributes: {
        tag: 'payment_error',
        data: {
          last_visited_page: this.PAYMENT_SECTION_NAME
        }
      },
    });
  }

  createTotalChangedLead(monthly, period, total) {
    this.createLead({
      email: this.leadEmail,
      lead_event_attributes: {
        tag: '',
        data: {
          monthly_amount: monthly,
          period_amount: period,
          total_amount: total
        }
      },
    });
  }

  private createLeadDataFromFormData(formData) {
    const res = { ...formData };
    this.forbiddenFormFieldNames.forEach((field) => delete res[field]);
    if (formData.address) { res.address = formData.address; }
    return res;
  }

  private createAddressAttributesFromFormData(formData) {
    const res: any = {};
    if (formData.street_name) { res.street_name = formData.street_name; }
    if (formData.city) { res.city = formData.city; }
    if (formData.state) { res.state = formData.state; }
    if (formData.postal_code) { res.zip_code = formData.postal_code; }
    return res;
  }

  private createProfileAttributesFromFormData(formData) {
    const res: any = {};
    if (formData.first_name) { res.first_name = formData.first_name; }
    if (formData.last_name) { res.last_name = formData.last_name; }
    if (formData.birth_date_main) { res.birth_date = formData.birth_date_main; }
    return res;
  }
}
